import { useRef, useState, type ElementType, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Camera, Check, Loader2, Lock, Package, RefreshCw, X } from 'lucide-react';
import { toBlob } from 'html-to-image';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CLASSES_SEGMENTO, type Segmento } from './segmentoTriagem';
import { useTriagemEmbutida } from './triagemEmbutida';
import { PlacarConsultasTriagem, useConsultasTriagem } from './consultaTriagem';
import { releTelasDeFora } from './acaoTriagem';

export interface TriagemShellLegenda {
  key: string;
  Icone: LucideIcon;
  label: string;
}

const GRAIN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")";

/** Pill translúcida do hero (69a): total dentro do vinho, não solto no branco. */
const PILL = 'flex h-[22px] flex-shrink-0 items-center gap-1 rounded-full border border-white/[0.14] bg-white/[0.12] px-2.5 text-[10.5px] text-white/70';
/** Utilitário do hero: quadrado 26px translúcido. */
/** Trilho do segmentado CNPJ/CPF no hero vinho: translúcido como as pills. */
export const SEG_TRILHO = 'relative inline-flex h-[24px] flex-shrink-0 items-center gap-[2px] rounded-[8px] border border-white/[0.14] bg-white/[0.12] p-[3px]';
export const SEG_BTN = 'flex h-[18px] items-center rounded-[5px] px-2 text-[9.5px] font-bold uppercase leading-none tracking-wider transition-colors';

const UTIL = 'grid h-[26px] w-[26px] flex-shrink-0 place-items-center rounded-lg border border-white/[0.14] bg-white/[0.12] text-white transition-colors hover:bg-white/[0.22] disabled:opacity-50';

interface TriagemShellProps {
  isOpen: boolean;
  onFechar: () => void;
  Icone: LucideIcon;
  titulo: string;
  /** Sub do hero (69a): "fila do quadro Tramitação Vivo · prazo 5h úteis". */
  subtitulo?: string;
  /**
   * `valor` é FATURAMENTO (KPI `FAT. TOTAL`). `aparelhos` anda SEPARADO, em selo
   * próprio — aparelho tem KPI próprio e não é faturamento de venda (Douglas
   * 17/08/2026: "não é junto, é separado"). Ver `flagsFaturamento.ts`.
   */
  contadores: { pedidos: number; itens: number; valor: number; aparelhos?: number };
  /** null quando a triagem não tem prazo (Ativação, NF, TT Recursos, Retenção…). */
  sla?: { horas: number | 'agendamento'; estourados: number } | null;
  fifo: boolean;
  /** Texto do selo de fila. Padrão: FIFO clássico (trabalhe o do topo). */
  fifoLabel?: string;
  /** Legenda dos ícones da LINHA — não são botões (ver comentário na barra). */
  legenda?: TriagemShellLegenda[];
  isLoading?: boolean;
  onAtualizar?: () => void;
  vazio?: { titulo: string; descricao: string };
  /** Texto do rodapé: idade do mais antigo da fila, já formatada. */
  maisAntigo?: { label: string; estourado: boolean } | null;
  /** Selo próprio da triagem ao lado dos contadores (ex.: "N sinalizados" na Biometria). */
  cabecalhoExtra?: ReactNode;
  /**
   * Segmento em que a fila está: CNPJ ou CPF, um por vez. Ver a lei em
   * `segmentoTriagem.ts`. Sem `onSegmento` o seletor não aparece — triagem que
   * ainda não separa não ganha um botão que não faz nada.
   */
  segmento?: Segmento;
  onSegmento?: (s: Segmento) => void;
  /** Liga o botão de copiar a tela como imagem (para cobrar no grupo do WhatsApp). */
  permitirPrint?: boolean;
  /**
   * EMBUTIDO (Douglas 22/09/2026): a mesma triagem, sem o modal por cima. É o
   * que a página Triagens usa para mostrar a fila ao lado da lista, em vez de
   * obrigar a pessoa a passar pelo Kanban. O conteúdo é idêntico — muda só a
   * moldura: sem Dialog, sem ✕ e sem a trava de ESC (não há o que travar).
   */
  embutido?: boolean;
  /**
   * O LOG DA CONSULTA (Douglas 30/09/2026): a chave da triagem no catálogo e
   * os pedidos que estão na tela. Com ela, o topo mostra se a fila foi
   * consultada nos 3 horários — medida pelos botões da linha (`AcoesTriagem`).
   * Ver `consultaTriagem.tsx`.
   */
  consulta?: { chave: string; pedidoIds: string[] };
  /** Colunas extras não cabem no shell: a tabela inteira vem de fora. */
  children: ReactNode;
}

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);

/**
 * O corpo único das 23 triagens (punch list `docs/design/punch-list-triagens.md`).
 *
 * Cada triagem passa só o que a diferencia — ícone, título, legenda e a tabela.
 * Cabeçalho, contadores, aviso de SLA, selo FIFO, estado vazio, rodapé e o
 * comportamento de fechar vivem AQUI, uma vez só.
 *
 * ⛔ O modal NÃO fecha por ESC nem por clique fora — só no ✕ (Douglas 06/08:
 * "o modal de triagem é frágil, ele teria que ficar firme na tela"). Quem está
 * ligando e cobrando cliente clica na tela o tempo todo, e fechar sem querer
 * fazia perder a fila e o lugar na leitura. O rodapé agora DIZ isso, em vez de
 * deixar a pessoa descobrir tentando.
 */
export function TriagemShell({
  isOpen,
  onFechar,
  embutido: embutidoProp,
  Icone,
  titulo,
  subtitulo,
  contadores,
  sla,
  fifo,
  fifoLabel,
  legenda,
  isLoading,
  onAtualizar,
  vazio,
  maisAntigo,
  cabecalhoExtra,
  segmento,
  onSegmento,
  permitirPrint,
  consulta,
  children,
}: TriagemShellProps) {
  const vazioAtivo = !!vazio;
  const telaRef = useRef<HTMLDivElement>(null);
  const embutidoDoContexto = useTriagemEmbutida();
  const [copiando, setCopiando] = useState(false);
  const [copiou, setCopiou] = useState(false);
  /* Os cliques da triagem não releem o Kanban (`acaoTriagem.ts`): ele se lê
     uma vez ao fechar, com tudo o que mudou. */
  const queryClient = useQueryClient();
  const fechar = () => {
    releTelasDeFora(queryClient);
    onFechar();
  };

  /**
   * Copia a TRIAGEM COMO ESTÁ NA TELA (com o filtro ligado) para o clipboard —
   * é assim que a cobrança vai para o grupo do WhatsApp: filtra, copia, cola.
   * Caminho: `toBlob` + ClipboardItem com a promise por dentro,
   * que mantém o gesto do usuário vivo no Chrome/Safari); sem clipboard de
   * imagem, baixa o PNG.
   */
  async function copiarTela() {
    const node = telaRef.current;
    if (!node) return;
    setCopiando(true);
    const opts = {
      pixelRatio: 2,
      cacheBust: true,
      backgroundColor: getComputedStyle(node).backgroundColor || '#ffffff',
      filter: (n: HTMLElement) => n?.dataset?.noCapture !== 'true',
    };
    try {
      if (navigator.clipboard && 'ClipboardItem' in window) {
        await navigator.clipboard.write([
          new ClipboardItem({
            'image/png': toBlob(node, opts).then((b) => {
              if (!b) throw new Error('sem imagem');
              return b;
            }),
          }),
        ]);
        setCopiou(true);
        setTimeout(() => setCopiou(false), 2200);
      } else {
        throw new Error('clipboard indisponível');
      }
    } catch {
      try {
        const blob = await toBlob(node, opts);
        if (blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${titulo.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.png`;
          a.click();
          URL.revokeObjectURL(url);
        }
      } catch (e) {
        console.error('[triagem] captura da tela falhou', e);
      }
    } finally {
      setCopiando(false);
    }
  }

  // O MESMO conteúdo nos dois modos — modal no Kanban, embutido na página
  // Triagens. Um corpo só: se a fila ganhar uma coluna, ela aparece nos dois.
  // A página Triagens liga o modo embutido pelo contexto; a prop existe para
  // quem quiser forçar um caso específico.
  const embutido = embutidoProp ?? embutidoDoContexto;
  const placar = useConsultasTriagem(
    consulta?.chave ?? null, consulta?.pedidoIds ?? [], segmento, !!isLoading, embutido || isOpen);
  const Titulo: ElementType = embutido ? 'h2' : DialogTitle;
  const corpo = (
    <>
        {/* ── HERO VINHO 58px (69a): totais em pills, utilitários translúcidos ── */}
        <div className="relative flex h-[58px] flex-shrink-0 items-center gap-3 overflow-hidden bg-[linear-gradient(135deg,#4A1320_0%,#5B0A0D_55%,#6E1626_100%)] px-[18px]">
          <span aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.12]" style={{ backgroundImage: GRAIN }} />
          <span className="relative grid h-8 w-8 flex-shrink-0 place-items-center rounded-[9px] bg-white/[0.12] text-white">
            <Icone className="h-[17px] w-[17px]" />
          </span>

          <div className="relative min-w-[150px] flex-shrink">
            <Titulo className="truncate font-display text-[16px] font-bold tracking-[-0.3px] text-white">
              {titulo}
            </Titulo>
            {subtitulo && <p className="truncate text-[10.5px] leading-tight text-white/60">{subtitulo}</p>}
          </div>

          <div className="relative flex flex-shrink-0 items-center gap-1.5">
            <span className={PILL}>
              <span className="font-display text-[11px] font-bold tabular-nums text-white">{contadores.pedidos}</span>
              pedido{contadores.pedidos === 1 ? '' : 's'}
            </span>
            <span className={PILL}>
              <span className="font-display text-[11px] font-bold tabular-nums text-white">{contadores.itens}</span>
              {contadores.itens === 1 ? 'item' : 'itens'}
            </span>
            {contadores.valor > 0 && (
              <span className={PILL}>
                <span className="font-display text-[11px] font-bold tabular-nums text-white">{formatCurrency(contadores.valor)}</span>
                na fila
              </span>
            )}
            {/* Aparelho FORA do R$ da fila, em pill própria: some do total, não
                some da tela — a fila precisa saber que tem aparelho parado ali. */}
            {(contadores.aparelhos ?? 0) > 0 && (
              <span className={PILL} title="Aparelhos na fila — contados à parte, fora do faturamento">
                <Package className="h-3 w-3" />
                <span className="font-display text-[11px] font-bold tabular-nums text-white">{contadores.aparelhos}</span>
                {contadores.aparelhos === 1 ? 'aparelho' : 'aparelhos'}
              </span>
            )}
          </div>

          <PlacarConsultasTriagem placar={placar} pill={PILL} />

          {/* ⛔ CNPJ e CPF, um por vez — e o contador ao lado já é do segmento
              escolhido, senão o print cobraria fila que não está na tela.
              Não existe "os dois": fila de trabalho misturada é o defeito. */}
          {segmento && onSegmento && (
            <span className={SEG_TRILHO}>
              {([['PJ', 'CNPJ'], ['PF', 'CPF']] as const).map(([valor, rotulo]) => {
                const ligado = segmento === valor;
                return (
                  <button
                    key={valor}
                    type="button"
                    onClick={() => onSegmento(valor)}
                    title={ligado ? `A fila está mostrando só ${rotulo}` : `Ver a fila de ${rotulo}`}
                    /* Ativo em chapa branca com a LETRA na cor da marca — no
                       hero vinho, cor sobre cor não se leria. */
                    className={cn(SEG_BTN, ligado ? `bg-white ${CLASSES_SEGMENTO[valor].texto}` : 'text-white/60 hover:text-white')}
                  >
                    {rotulo}
                  </button>
                );
              })}
            </span>
          )}

          {cabecalhoExtra}

          {/* SLA à direita. Sem prazo, o bloco inteiro some — não vira "—". */}
          {sla && (
            <span className="relative ml-auto flex flex-shrink-0 items-center gap-1.5 text-[10.5px]">
              <span
                className={cn(
                  'h-[5px] w-[5px] rounded-full',
                  sla.estourados > 0 ? 'bg-[#FF9A9A]' : 'bg-[#8FE3A8]',
                )}
              />
              <span className="text-white/70">
                SLA {sla.horas === 'agendamento' ? 'por agenda' : `${sla.horas}h`}
              </span>
              {sla.estourados > 0 && (
                <>
                  <span className="text-white/40">·</span>
                  <span className="font-bold tabular-nums text-[#FF9A9A]">
                    {sla.estourados} estourado{sla.estourados === 1 ? '' : 's'}
                  </span>
                </>
              )}
            </span>
          )}

          {/* Utilitários ficam FORA da imagem: quem recebe o print no grupo não
              precisa ver os botões de atualizar/fechar da tela de quem mandou. */}
          <div
            data-no-capture="true"
            className={cn('relative flex flex-shrink-0 items-center gap-1.5', !sla && 'ml-auto')}
          >
            {permitirPrint && (
              <button
                type="button"
                className={cn(UTIL, copiou && 'bg-white text-[#5B0A0D]')}
                title="Copiar esta tela como imagem — cola direto no WhatsApp (Ctrl+V)"
                onClick={copiarTela}
                disabled={copiando}
              >
                {copiando ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : copiou ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Camera className="h-3.5 w-3.5" />
                )}
              </button>
            )}
            {onAtualizar && (
              <button
                type="button"
                className={UTIL}
                title="Atualizar a fila"
                onClick={onAtualizar}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" />
                )}
              </button>
            )}
            <button
              type="button"
              className={UTIL}
              title="Fechar a triagem — Esc não fecha, só este ✕"
              onClick={fechar}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* ── CORPO bone com card branco (69a): a tabela não fica solta no branco ── */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[#F1EDE7] p-3 dark:bg-background">
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-[#E7DCCB] bg-card dark:border-border">
            <div className="min-h-0 flex-1 overflow-auto [&>table]:min-w-[1120px]">
              {isLoading ? (
                <div className="flex min-h-[140px] items-center justify-center py-10">
                  <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
                </div>
              ) : vazioAtivo ? (
                /* Estado vazio de 140px — antes ocupava ~500px de modal só para
                   dizer "Nenhum pedido pendente". */
                <div className="flex min-h-[140px] flex-col items-center justify-center gap-2 px-4 py-8 text-center">
                  <span className="grid h-11 w-11 place-items-center rounded-full bg-accent">
                    <Check className="h-[18px] w-[18px] text-primary" />
                  </span>
                  <p className="text-[13px] font-bold text-foreground">{vazio.titulo}</p>
                  <p className="text-[11.5px] text-muted-foreground">{vazio.descricao}</p>
                  {/* Fila vazia com filtro ligado engana: quem não lembra do
                      seletor conclui que a fila zerou. O estado vazio DIZ qual
                      segmento está na tela e oferece o outro. */}
                  {segmento && onSegmento && (
                    <button
                      type="button"
                      onClick={() => onSegmento(segmento === 'PJ' ? 'PF' : 'PJ')}
                      className="text-[10.5px] font-semibold text-primary underline-offset-2 hover:underline"
                    >
                      Mostrando só {segmento === 'PJ' ? 'CNPJ' : 'CPF'} — ver a fila de {segmento === 'PJ' ? 'CPF' : 'CNPJ'}
                    </button>
                  )}
                </div>
              ) : (
                children
              )}
            </div>

            {/* Rodapé do card: a legenda saiu do topo (69a) — aqui ela explica os
                ícones da LINHA sem parecer botão, junto do selo FIFO e do prazo. */}
            {(fifo || (legenda && legenda.length > 0) || sla) && (
              <div className="flex h-[26px] flex-shrink-0 flex-nowrap items-center gap-3 overflow-hidden border-t border-border-warm px-3.5 text-[10px] text-muted-foreground">
                {fifo && (
                  <span className="flex flex-shrink-0 items-center gap-1 font-semibold">
                    <Lock className="h-3 w-3" />
                    {fifoLabel || 'FIFO — trabalhe o do topo'}
                  </span>
                )}
                {legenda?.map((l) => (
                  <span key={l.key} className="flex flex-shrink-0 items-center gap-1 font-semibold">
                    <l.Icone className="h-3 w-3 flex-shrink-0" />
                    {l.label}
                  </span>
                ))}
                {sla && (
                  <span className="ml-auto flex-shrink-0 text-muted-foreground/70">
                    prazo desta fila: {sla.horas === 'agendamento' ? 'por agenda' : `${sla.horas}h úteis`}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── RODAPÉ 46px ─────────────────────────────────────────────────── */}
        <div className="flex h-[46px] flex-shrink-0 flex-nowrap items-center gap-2 overflow-hidden border-t border-border-warm bg-[#FBFAF9] px-[18px] text-[10.5px] dark:bg-white/[0.02]">
          <span className="flex-shrink-0 text-muted-foreground">
            <span className="font-bold tabular-nums text-foreground">{contadores.pedidos}</span>{' '}
            {contadores.pedidos === 1 ? 'pedido na fila' : 'pedidos na fila'}
          </span>
          {contadores.valor > 0 && (
            <>
              <span className="flex-shrink-0 text-muted-foreground/40">·</span>
              <span className="flex-shrink-0 text-muted-foreground">
                Total{' '}
                <span className="font-display font-bold tabular-nums text-money-highlight">
                  {formatCurrency(contadores.valor)}
                </span>
              </span>
            </>
          )}
          {maisAntigo && (
            <>
              <span className="flex-shrink-0 text-muted-foreground/40">·</span>
              <span className="flex flex-shrink-0 items-center gap-1 text-muted-foreground">
                <span
                  className={cn(
                    'h-[5px] w-[5px] rounded-full',
                    maisAntigo.estourado ? 'bg-danger' : 'bg-success',
                  )}
                />
                mais antigo {maisAntigo.label}
              </span>
            </>
          )}
        </div>
    </>
  );

  if (embutido) {
    return (
      <div
        ref={telaRef}
        className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-border-warm bg-surface"
      >
        {corpo}
      </div>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && fechar()}>
      {/* ⛔ A altura CABE NA TELA, nunca e' numero fixo.
          Era um teto fixo de 760px (⚠️ escrito sem a sintaxe de classe de
          proposito: o Tailwind varre este arquivo como texto e geraria a classe
          morta so' por causa da citacao num comentario).
          Num notebook 1366x768 sobram ~600px uteis, o modal
          pedia 760, e como o Dialog centraliza com translate(-50%,-50%) o
          CABECALHO saia por cima da tela — levando junto o unico botao de fechar.
          Somado ao ESC e ao clique-fora bloqueados logo abaixo, o usuario ficava
          PRESO dentro do modal (Douglas, 23/08, num PC que nao e' o Mac dele).
          O 760 segue como teto em tela grande; o min() so' impede passar do que
          existe de viewport. Era o unico modal do app com altura fixa em px —
          os outros 54 ja usavam vh, e o padrao `min(Npx,88vh)` vem dos modais do
          BKO (NotasEquipeModal, NotaBkoDetalheModal). Mesma regua, nao uma nova. */}
      {/* ⚠️ SEM translate(-50%,-50%): centraliza por inset+margin auto. No
          Windows (Chrome com escala 125%), o -50% deixava o modal em MEIO
          PIXEL e, ao rolar a lista, a camada de GPU rasterizava o texto fora
          da grade — a triagem inteira ficava embaçada (Lisiane, 29/08).
          Centralização por layout faz snap no pixel; transform não. */}
      <DialogContent
        className="inset-0 left-0 top-0 m-auto flex h-[calc(100dvh-16px)] max-h-none w-[calc(100vw-16px)] max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-2xl border border-border-warm bg-surface p-0 shadow-premium data-[state=closed]:slide-out-to-left-0 data-[state=closed]:slide-out-to-top-0 data-[state=open]:slide-in-from-left-0 data-[state=open]:slide-in-from-top-0 sm:h-[min(900px,calc(100dvh-32px))] sm:w-[calc(100vw-32px)] sm:max-w-none 2xl:max-w-[1536px] [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        ref={telaRef}
      >
        {corpo}
      </DialogContent>
    </Dialog>
  );
}
